import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import { AlertTriangle, CheckCircle, RefreshCw, Merge, Info, Edit3, Database, Sparkles, List, ChevronDown, ChevronUp, Zap, Trash2, Wrench, RefreshCcw } from 'lucide-react';

const BACKEND_URL = getBackendUrl();

// Helper: Clone response before reading body to prevent "Response body is already used" errors
// caused by platform session recorders consuming the body first
const safeJson = async (res) => {
  try {
    return await res.clone().json();
  } catch {
    return await res.json();
  }
};

const CanonicalConflictResolver = () => {
  const { t } = useLanguage();
  const [conflicts, setConflicts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Ref to track if component is mounted
  const isMounted = useRef(true);
  
  // Manual override state
  const [overrideMode, setOverrideMode] = useState(null); // normalized_key when active
  const [customArtist, setCustomArtist] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [userDbSuggestions, setUserDbSuggestions] = useState({});
  const [loadingSuggestions, setLoadingSuggestions] = useState({});
  
  // Expanded variations state
  const [expandedVariations, setExpandedVariations] = useState({});
  
  // Auto-resolve state
  const [autoResolving, setAutoResolving] = useState(false);
  const [autoResolvePreview, setAutoResolvePreview] = useState(null);

  // Cleanup non-songs state
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  
  // Repair aliases state
  const [repairing, setRepairing] = useState(false);
  const [repairPreview, setRepairPreview] = useState(null);

  // Refresh normalized keys state
  const [refreshingKeys, setRefreshingKeys] = useState(false);
  const [refreshPreview, setRefreshPreview] = useState(null);

  // Greeklish TC Auto-Resolve state
  const [greeklishResolving, setGreeklishResolving] = useState(false);
  const [greeklishResult, setGreeklishResult] = useState(null);

  // Clear orphan flags state
  const [clearingOrphans, setClearingOrphans] = useState(false);

  // Fetch User DB suggestions for a conflict - using ALL variations
  const fetchUserDbSuggestionForConflict = useCallback(async (conflict, existingSuggestions) => {
    const key = conflict.normalized_key;
    if (existingSuggestions && existingSuggestions[key]) return; // Already fetched
    
    setLoadingSuggestions(prev => ({ ...prev, [key]: true }));
    
    try {
      // Collect ALL unique artist/title pairs from variations and samples
      const searchPairs = [];
      
      // From all_variations (new field)
      if (conflict.all_variations?.length > 0) {
        conflict.all_variations.forEach(v => {
          if (v.artist && v.title) {
            searchPairs.push({ artist: v.artist, title: v.title });
          }
        });
      }
      
      // From sample_artists and sample_titles (fallback)
      if (searchPairs.length === 0) {
        const artists = conflict.sample_artists || [];
        const titles = conflict.sample_titles || [];
        artists.forEach(artist => {
          titles.forEach(title => {
            // Clean up "artist1 / artist2" format
            const cleanArtist = artist.split(' / ')[0];
            const cleanTitle = title.split(' / ')[0];
            if (cleanArtist && cleanTitle) {
              searchPairs.push({ artist: cleanArtist, title: cleanTitle });
            }
          });
        });
      }
      
      if (searchPairs.length === 0) {
        setLoadingSuggestions(prev => ({ ...prev, [key]: false }));
        setUserDbSuggestions(prev => ({ ...prev, [key]: [] }));
        return;
      }
      
      // Search with each pair and collect unique suggestions
      const allSuggestions = [];
      const seenKeys = new Set();
      
      // Limit to first 5 pairs to avoid too many requests
      for (const pair of searchPairs.slice(0, 5)) {
        try {
          const response = await fetch(
            `${BACKEND_URL}/api/canonical-conflicts/user-db-suggestion?artist=${encodeURIComponent(pair.artist)}&title=${encodeURIComponent(pair.title)}`
          );
          
          if (!response.ok) {
            console.warn('Suggestion API error:', response.status);
            continue;
          }
          
          const data = await safeJson(response);
          
          if (data.suggestions?.length > 0) {
            data.suggestions.forEach(s => {
              const suggKey = `${s.artist.toUpperCase()}|${s.title.toUpperCase()}`;
              if (!seenKeys.has(suggKey)) {
                seenKeys.add(suggKey);
                allSuggestions.push(s);
              }
            });
          }
        } catch (innerErr) {
          console.warn('Error fetching suggestion for pair:', pair, innerErr);
        }
      }
      
      // Sort by match_score descending
      allSuggestions.sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
      
      setUserDbSuggestions(prev => ({
        ...prev,
        [key]: allSuggestions.slice(0, 10) // Keep top 10
      }));
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setUserDbSuggestions(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingSuggestions(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  const fetchConflicts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch conflicts and stats separately to avoid response cloning issues
      const conflictsRes = await fetch(`${BACKEND_URL}/api/canonical-conflicts/pending`);
      if (!conflictsRes.ok) {
        throw new Error(`HTTP ${conflictsRes.status}`);
      }
      const conflictsData = await safeJson(conflictsRes);
      
      const statsRes = await fetch(`${BACKEND_URL}/api/canonical-conflicts/stats`);
      if (!statsRes.ok) {
        throw new Error(`HTTP ${statsRes.status}`);
      }
      const statsData = await safeJson(statsRes);
      
      const conflictsList = conflictsData.conflicts || [];
      setConflicts(conflictsList);
      setStats(statsData);
      
      // Auto-fetch User DB suggestions for all conflicts (sequential to avoid issues)
      for (const conflict of conflictsList) {
        await fetchUserDbSuggestionForConflict(conflict, {});
      }
      
    } catch (err) {
      console.error('fetchConflicts error:', err);
      setError(t('loadConflictsError') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchUserDbSuggestionForConflict]);

  useEffect(() => {
    isMounted.current = true;
    fetchConflicts();
    
    return () => {
      isMounted.current = false;
    };
  }, [fetchConflicts]);

  // Legacy function for manual refresh
  const fetchUserDbSuggestion = (conflict) => {
    fetchUserDbSuggestionForConflict(conflict, userDbSuggestions);
  };

  const runDetection = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/detect?dry_run=false`, {
        method: 'POST'
      });
      const data = await safeJson(response);
      setSuccess(`Εντοπίστηκαν ${data.conflicts_detected} conflicts, σημειώθηκαν ${data.plays_flagged} plays`);
      await fetchConflicts();
    } catch (err) {
      setError(t('errorGeneric') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resolveConflict = async (normalizedKey, winnerCanonicalId, conflict = null) => {
    setResolving(normalizedKey);
    setError(null);
    try {
      // Build request body - include recommended_display if available
      const body = {
        normalized_key: normalizedKey,
        winner_canonical_id: winnerCanonicalId,
        admin_user: 'admin'
      };
      
      // If this is the recommended option and we have the correct display format, include it
      if (conflict && conflict.recommended_display && 
          conflict.recommended_canonical_id === winnerCanonicalId) {
        body.override_display = {
          artist: conflict.recommended_display.artist,
          title: conflict.recommended_display.title
        };
      }
      
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error('Resolution failed');
      }
      
      const data = await safeJson(response);
      setSuccess(`Επιλύθηκε: ${data.plays_updated} plays ενημερώθηκαν, ${data.canonicals_merged} canonicals συγχωνεύτηκαν`);
      
      // Remove resolved conflict from list
      setConflicts(prev => prev.filter(c => c.normalized_key !== normalizedKey));
    } catch (err) {
      setError(t('resolveError') + ': ' + err.message);
    } finally {
      setResolving(null);
    }
  };

  // Manual Override resolution
  const resolveWithOverride = async (normalizedKey) => {
    if (!customArtist.trim() || !customTitle.trim()) {
      setError('Εισάγετε και Artist και Title');
      return;
    }
    
    setResolving(normalizedKey);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/resolve-with-override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          normalized_key: normalizedKey,
          custom_artist: customArtist.trim(),
          custom_title: customTitle.trim(),
          admin_user: 'admin'
        })
      });
      
      if (!response.ok) {
        const errData = await safeJson(response);
        throw new Error(errData.detail || 'Override failed');
      }
      
      const data = await safeJson(response);
      setSuccess(`✅ Manual Override: "${customArtist} - ${customTitle}" - ${data.plays_updated} plays ενημερώθηκαν`);
      
      // Reset override mode and clear fields
      setOverrideMode(null);
      setCustomArtist('');
      setCustomTitle('');
      
      // Remove resolved conflict from list
      setConflicts(prev => prev.filter(c => c.normalized_key !== normalizedKey));
    } catch (err) {
      setError(t('overrideError') + ': ' + err.message);
    } finally {
      setResolving(null);
    }
  };

  // Apply User DB suggestion to override fields
  const applySuggestion = (suggestion) => {
    setCustomArtist(suggestion.artist);
    setCustomTitle(suggestion.title);
  };

  // Direct resolution with User DB suggestion (no manual editing needed)
  const resolveWithUserDbSuggestion = async (normalizedKey, suggestion) => {
    setResolving(normalizedKey);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/resolve-with-override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          normalized_key: normalizedKey,
          custom_artist: suggestion.artist,
          custom_title: suggestion.title,
          admin_user: 'admin'
        })
      });
      
      if (!response.ok) {
        const errData = await safeJson(response);
        throw new Error(errData.detail || 'Resolution failed');
      }
      
      const data = await safeJson(response);
      setSuccess(`✅ Επιλέχθηκε από User DB: "${suggestion.artist} - ${suggestion.title}" - ${data.plays_updated} plays ενημερώθηκαν`);
      
      // Remove resolved conflict from list
      setConflicts(prev => prev.filter(c => c.normalized_key !== normalizedKey));
    } catch (err) {
      setError(t('resolveError') + ': ' + err.message);
    } finally {
      setResolving(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // Greeklish Title Case Auto-Resolve - One-click resolution
  // Uses certified_display_names + auto-conversion to Greeklish TC
  // ═══════════════════════════════════════════════════════════════
  const runGreeklishAutoResolve = async () => {
    setGreeklishResolving(true);
    setError(null);
    setGreeklishResult(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/auto-resolve-greeklish-tc?max_conflicts=200`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await safeJson(response);
      if (response.ok) {
        setGreeklishResult(data);
        setSuccess(`✅ Greeklish TC: ${data.conflicts_resolved}/${data.conflicts_processed} resolved, ${data.plays_updated} plays updated`);
        fetchConflicts();
      } else {
        setError('Greeklish Auto-Resolve failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (err) {
      setError('Greeklish Auto-Resolve error: ' + err.message);
    } finally {
      setGreeklishResolving(false);
    }
  };

  // Clear orphan flagged plays
  const clearOrphanFlags = async () => {
    setClearingOrphans(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/dashboard/flagged-plays/clear-orphans`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await safeJson(response);
      if (response.ok) {
        setSuccess(`Cleared ${data.cleared_count} orphan flagged plays`);
        fetchConflicts();
      } else {
        setError(data.detail || 'Failed to clear orphan flags');
      }
    } catch (err) {
      setError('Clear orphan flags error: ' + err.message);
    } finally {
      setClearingOrphans(false);
    }
  };

  // Auto-resolve preview - see what would be resolved
  const runAutoResolvePreview = async () => {
    setAutoResolving(true);
    setError(null);
    setAutoResolvePreview(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/auto-resolve/preview?max_conflicts=100`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await safeJson(response);
      setAutoResolvePreview(data);
      
      if (data.will_auto_resolve > 0) {
        setSuccess(`🔍 Preview: ${data.will_auto_resolve} conflicts μπορούν να επιλυθούν αυτόματα με User DB`);
      } else {
        setSuccess('ℹ️ Δεν βρέθηκαν conflicts με exact match στο User DB');
      }
    } catch (err) {
      console.error('Auto-resolve preview error:', err);
      setError(t('previewError') + ': ' + err.message);
    } finally {
      setAutoResolving(false);
    }
  };

  // Auto-resolve run - actually resolve conflicts
  const runAutoResolve = async () => {
    if (!window.confirm('Θέλετε να επιλύσετε αυτόματα όλα τα conflicts που έχουν exact match στο User DB;')) {
      return;
    }
    
    setAutoResolving(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/auto-resolve/run?max_conflicts=100`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await safeJson(response);
      
      setSuccess(`✅ Auto-Resolve: ${data.resolved_count} conflicts επιλύθηκαν, ${data.total_plays_updated} plays ενημερώθηκαν`);
      setAutoResolvePreview(null);
      
      // Refresh conflicts list
      await fetchConflicts();
    } catch (err) {
      console.error('Auto-resolve run error:', err);
      setError(t('autoResolveError') + ': ' + err.message);
    } finally {
      setAutoResolving(false);
    }
  };

  // Cleanup non-songs (jingles, promos, etc.)
  const runCleanupPreview = async () => {
    setCleaningUp(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/cleanup-non-songs?dry_run=true`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Cleanup preview failed');
      
      const data = await safeJson(response);
      setCleanupPreview(data);
    } catch (err) {
      setError(t('cleanupPreviewError') + ': ' + err.message);
    } finally {
      setCleaningUp(false);
    }
  };

  const runCleanup = async () => {
    if (!window.confirm(`Θέλετε να αφαιρέσετε ${cleanupPreview?.non_songs_found} jingles/promos από τα conflicts;`)) {
      return;
    }
    
    setCleaningUp(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/cleanup-non-songs?dry_run=false`, {
        method: 'POST'
      });
      
      const data = await safeJson(response);
      setSuccess(`✅ Cleanup: ${data.removed} non-songs αφαιρέθηκαν`);
      setCleanupPreview(null);
      await fetchConflicts();
    } catch (err) {
      setError(t('cleanupError') + ': ' + err.message);
    } finally {
      setCleaningUp(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // NOTE: Broad jingle cleanup is now a fully-automated scheduled job
  // (runs nightly at 03:30 — see server.py `jingle_auto_cleanup_job`).
  // The manual button + preview panel were removed at user's request
  // ("δεν είμαι μηχανή να παρακολουθώ 24/7").
  // ═══════════════════════════════════════════════════════════════

  // Repair missing aliases for resolved conflicts
  const runRepairPreview = async () => {
    setRepairing(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/repair-aliases?dry_run=true`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Repair preview failed');
      
      const data = await safeJson(response);
      setRepairPreview(data);
    } catch (err) {
      setError(t('repairPreviewError') + ': ' + err.message);
    } finally {
      setRepairing(false);
    }
  };

  const runRepair = async () => {
    if (!window.confirm(`Θέλετε να δημιουργήσετε ${repairPreview?.missing_aliases_found} missing aliases;`)) {
      return;
    }
    
    setRepairing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/repair-aliases?dry_run=false`, {
        method: 'POST'
      });
      
      const data = await safeJson(response);
      setSuccess(`✅ Repair: ${data.aliases_created} aliases δημιουργήθηκαν`);
      setRepairPreview(null);
    } catch (err) {
      setError(t('repairError') + ': ' + err.message);
    } finally {
      setRepairing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // REFRESH NORMALIZED KEYS - Critical for fixing hidden duplicates
  // ═══════════════════════════════════════════════════════════════════════════════
  const runRefreshPreview = async () => {
    setRefreshingKeys(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/refresh-normalized-keys?dry_run=true`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Refresh preview failed');
      
      const data = await safeJson(response);
      console.log('Refresh Keys API response:', data);
      
      // API returns stats nested under "stats" key
      // Handle both formats: {stats: {...}} or direct {...}
      const preview = data.stats ? data.stats : data;
      console.log('Setting refreshPreview to:', preview);
      setRefreshPreview({
        mismatches_found: preview.mismatches_found ?? 0,
        total_checked: preview.total_checked ?? 0,
        sample_mismatches: preview.sample_mismatches || [],
        dry_run: preview.dry_run ?? true
      });
    } catch (err) {
      console.error('Refresh preview error:', err);
      setError(t('refreshPreviewError') + ': ' + err.message);
    } finally {
      setRefreshingKeys(false);
    }
  };

  const runRefresh = async () => {
    if (!window.confirm(`ΠΡΟΣΟΧΗ: Θα ενημερωθούν ${refreshPreview?.mismatches_found} normalized keys.\n\nΑυτή η διαδικασία μπορεί να διαρκέσει αρκετά λεπτά.\n\nΣυνέχεια;`)) {
      return;
    }
    
    setRefreshingKeys(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/canonical-conflicts/refresh-normalized-keys?dry_run=false`, {
        method: 'POST'
      });
      
      const data = await safeJson(response);
      setSuccess(`✅ Refresh: ${data.updated} normalized keys ενημερώθηκαν. Τρέξτε "Εντοπισμός Conflicts" για να δείτε τα νέα conflicts.`);
      setRefreshPreview(null);
      // Auto-run detection after refresh
      setTimeout(() => runDetection(), 1000);
    } catch (err) {
      setError(t('refreshError') + ': ' + err.message);
    } finally {
      setRefreshingKeys(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="text-yellow-400" />
            Canonical Conflict Resolution
          </h2>
          <p className="text-gray-400 mt-1">
            ZERO-LOSS: Conflicts require manual resolution before aggregation
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={fetchConflicts}
            disabled={loading}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Ανανέωση
          </button>
          <button
            onClick={runDetection}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2"
          >
            Εντοπισμός Conflicts
          </button>
          {/* v2.6.4 (2026-05-16) — Confidence Gate live; following 3 buttons
              were retroactive auto-fix tools that now conflict with the
              proactive gate and have been retired:
                • Auto-Resolve Preview (purple)
                • Greeklish TC Resolve (green)
                • Cleanup Non-Songs (orange)
              Their backend endpoints remain in place for emergency rollback
              but are no longer surfaced in the UI. */}
          <button
            onClick={runRepairPreview}
            disabled={repairing || loading}
            className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center gap-2"
            title="Επιδιορθώνει missing aliases για ήδη resolved conflicts"
          >
            {repairing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            Repair Aliases
          </button>
          <button
            onClick={runRefreshPreview}
            disabled={refreshingKeys || loading}
            className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg flex items-center gap-2"
            title="ΚΡΙΣΙΜΟ: Επανυπολογίζει όλα τα normalized keys με τη νέα λογική - Διορθώνει διπλότυπα όπως Latrevo/Latreyo"
          >
            {refreshingKeys ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            🔥 Refresh Keys
          </button>
          <button
            onClick={clearOrphanFlags}
            disabled={clearingOrphans || loading}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg flex items-center gap-2"
            title="Clear flagged plays that no longer belong to any active conflict"
            data-testid="clear-orphan-flags-btn"
          >
            {clearingOrphans ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Clear Orphan Flags
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-3xl font-bold text-yellow-400">{stats.pending_review}</div>
            <div className="text-gray-400 text-sm">Pending Review</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-3xl font-bold text-green-400">{stats.resolved}</div>
            <div className="text-gray-400 text-sm">Resolved</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-3xl font-bold text-red-400">{stats.flagged_plays}</div>
            <div className="text-gray-400 text-sm">Flagged Plays</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-white">{stats.status}</div>
            <div className="text-gray-400 text-sm">Status</div>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Auto-Resolve Preview Panel */}
      {autoResolvePreview && (
        <div className="bg-purple-900/30 border border-purple-500 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-400" />
              <span className="text-purple-200 font-medium">Auto-Resolve Preview</span>
            </div>
            <button
              onClick={() => setAutoResolvePreview(null)}
              className="text-gray-400 hover:text-white text-sm"
            >
              ✕ Κλείσιμο
            </button>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-400">{autoResolvePreview.will_auto_resolve}</div>
              <div className="text-xs text-gray-400">Θα επιλυθούν</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-yellow-400">{autoResolvePreview.will_skip}</div>
              <div className="text-xs text-gray-400">Θα παραμείνουν</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-400">{autoResolvePreview.total_checked}</div>
              <div className="text-xs text-gray-400">Ελέγχθηκαν</div>
            </div>
          </div>
          
          {autoResolvePreview.will_auto_resolve > 0 && (
            <>
              <div className="text-sm text-purple-200 mb-2">Παραδείγματα που θα επιλυθούν:</div>
              <div className="max-h-40 overflow-y-auto space-y-1 mb-4">
                {autoResolvePreview.resolve_list?.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="text-sm bg-gray-800/30 rounded px-3 py-2 flex justify-between">
                    <span className="text-gray-300">{item.normalized_key}</span>
                    <span className="text-green-400">→ {item.will_use?.display}</span>
                  </div>
                ))}
              </div>
              
              <button
                onClick={runAutoResolve}
                disabled={autoResolving}
                className="w-full py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center justify-center gap-2 font-medium"
              >
                {autoResolving ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Zap className="w-5 h-5" />
                )}
                Εκτέλεση Auto-Resolve ({autoResolvePreview.will_auto_resolve} conflicts)
              </button>
            </>
          )}
        </div>
      )}

      {/* Greeklish TC Resolve Results Panel */}
      {greeklishResult && (
        <div data-testid="greeklish-result-panel" className="bg-green-900/30 border border-green-500 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-400" />
              <span className="text-green-200 font-medium">Greeklish Title Case Auto-Resolve</span>
            </div>
            <button
              onClick={() => setGreeklishResult(null)}
              className="text-gray-400 hover:text-white text-sm"
            >
              Close
            </button>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-400">{greeklishResult.conflicts_resolved}</div>
              <div className="text-xs text-gray-400">Resolved</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-400">{greeklishResult.conflicts_processed}</div>
              <div className="text-xs text-gray-400">Processed</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-yellow-400">{greeklishResult.conflicts_skipped}</div>
              <div className="text-xs text-gray-400">Skipped</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-purple-400">{greeklishResult.plays_updated}</div>
              <div className="text-xs text-gray-400">Plays Updated</div>
            </div>
          </div>
        </div>
      )}

      {/* Cleanup Non-Songs Preview Panel */}
      {cleanupPreview && (
        <div className="bg-orange-900/30 border border-orange-500 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-orange-400" />
              <span className="text-orange-200 font-medium">Cleanup Non-Songs Preview</span>
            </div>
            <button
              onClick={() => setCleanupPreview(null)}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-orange-400">{cleanupPreview.non_songs_found}</div>
              <div className="text-xs text-gray-400">Non-songs βρέθηκαν</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-400">{cleanupPreview.total_conflicts}</div>
              <div className="text-xs text-gray-400">Συνολικά Conflicts</div>
            </div>
          </div>
          
          {cleanupPreview.non_songs_found > 0 && (
            <>
              <div className="text-sm text-orange-200 mb-2">Παραδείγματα που θα αφαιρεθούν:</div>
              <div className="max-h-40 overflow-y-auto space-y-1 mb-4">
                {cleanupPreview.examples?.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="text-sm bg-gray-800/30 rounded px-3 py-2">
                    <span className="text-gray-300">{item.normalized_key}</span>
                    <span className="text-orange-400 ml-2">({item.content_type})</span>
                  </div>
                ))}
              </div>
              
              <button
                onClick={runCleanup}
                disabled={cleaningUp}
                className="w-full py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-lg flex items-center justify-center gap-2 font-medium"
              >
                {cleaningUp ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
                Αφαίρεση {cleanupPreview.non_songs_found} Non-Songs
              </button>
            </>
          )}
          
          {cleanupPreview.non_songs_found === 0 && (
            <div className="text-center text-green-400 py-2">
              <CheckCircle className="w-6 h-6 mx-auto mb-1" />
              Δεν βρέθηκαν jingles/promos στα conflicts!
            </div>
          )}
        </div>
      )}

      {/* Repair Aliases Preview Panel */}
      {repairPreview && (
        <div className="bg-green-900/30 border border-green-500 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-green-400" />
              <span className="text-green-200 font-medium">Repair Aliases Preview</span>
            </div>
            <button
              onClick={() => setRepairPreview(null)}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-400">{repairPreview.missing_aliases_found}</div>
              <div className="text-xs text-gray-400">Missing Aliases</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-400">{repairPreview.resolved_conflicts}</div>
              <div className="text-xs text-gray-400">Resolved Conflicts</div>
            </div>
          </div>
          
          {repairPreview.missing_aliases_found > 0 && (
            <>
              <div className="text-sm text-green-200 mb-2">Παραδείγματα που θα επιδιορθωθούν:</div>
              <div className="max-h-40 overflow-y-auto space-y-1 mb-4">
                {repairPreview.details?.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="text-sm bg-gray-800/30 rounded px-3 py-2">
                    <span className="text-gray-300">{item.song}</span>
                    <span className="text-green-400 ml-2">→ {item.missing_aliases} aliases</span>
                  </div>
                ))}
              </div>
              
              <button
                onClick={runRepair}
                disabled={repairing}
                className="w-full py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg flex items-center justify-center gap-2 font-medium"
              >
                {repairing ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Wrench className="w-5 h-5" />
                )}
                Δημιουργία {repairPreview.missing_aliases_found} Aliases
              </button>
            </>
          )}
          
          {repairPreview.missing_aliases_found === 0 && (
            <div className="text-center text-green-400 py-2">
              <CheckCircle className="w-6 h-6 mx-auto mb-1" />
              Όλα τα aliases είναι σωστά!
            </div>
          )}
        </div>
      )}

      {/* Refresh Normalized Keys Preview Panel - CRITICAL FIX */}
      {refreshPreview && (
        <div className="bg-red-900/30 border border-red-500 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <RefreshCcw className="w-5 h-5 text-red-400" />
              <span className="text-red-200 font-medium">🔥 Refresh Normalized Keys Preview</span>
            </div>
            <button
              onClick={() => setRefreshPreview(null)}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          
          <div className="bg-red-950/50 border border-red-700 rounded p-3 mb-4 text-sm text-red-200">
            <strong>ΚΡΙΣΙΜΟ:</strong> Αυτή η λειτουργία διορθώνει τα "κρυφά διπλότυπα" όπως:<br/>
            • "Gia Sena Pou Latrevo" vs "Gia Sena pou Latreyo"<br/>
            • Τραγούδια που εμφανίζονται ξεχωριστά στο Compare Stations αλλά είναι ίδια
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-400">{refreshPreview.mismatches_found ?? 0}</div>
              <div className="text-xs text-gray-400">Keys προς ενημέρωση</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-400">{refreshPreview.total_checked ?? 0}</div>
              <div className="text-xs text-gray-400">Συνολικά ελέγχθηκαν</div>
            </div>
          </div>
          
          {refreshPreview.mismatches_found > 0 && (
            <>
              <div className="text-sm text-red-200 mb-2">Παραδείγματα που θα αλλάξουν:</div>
              <div className="max-h-40 overflow-y-auto space-y-1 mb-4">
                {refreshPreview.sample_mismatches?.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="text-sm bg-gray-800/30 rounded px-3 py-2">
                    <div className="text-gray-300">{item.artist} - {item.title}</div>
                    <div className="text-xs mt-1">
                      <span className="text-red-400">Old: {item.old_key}</span>
                      <span className="text-green-400 ml-2">→ New: {item.new_key}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={runRefresh}
                disabled={refreshingKeys}
                className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg flex items-center justify-center gap-2 font-medium"
              >
                {refreshingKeys ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <RefreshCcw className="w-5 h-5" />
                )}
                🔥 Εκτέλεση Refresh ({refreshPreview.mismatches_found} keys)
              </button>
            </>
          )}
          
          {refreshPreview.mismatches_found === 0 && (
            <div className="text-center text-green-400 py-2">
              <CheckCircle className="w-6 h-6 mx-auto mb-1" />
              Όλα τα normalized keys είναι ενημερωμένα!
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-blue-200 text-sm">
          <strong>ZERO-LOSS Policy:</strong> {t('conflictDescription')}
        </div>
      </div>

      {/* Conflicts List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
          {t('loadingConflicts')}
        </div>
      ) : conflicts.length === 0 ? (
        <div className="text-center py-12 text-gray-400 bg-gray-800 rounded-lg">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <p className="text-xl font-medium text-white">Δεν υπάρχουν conflicts!</p>
          <p className="text-gray-400 mt-2">Όλα τα τραγούδια έχουν μοναδικό canonical identity.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {conflicts.map((conflict) => (
            <div 
              key={conflict.normalized_key}
              className="bg-gray-800 rounded-lg p-5 border border-gray-700"
              onMouseEnter={() => fetchUserDbSuggestion(conflict)}
            >
              {/* Conflict Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-sm text-gray-500 font-mono mb-1">
                    normalized_key: {conflict.normalized_key}
                  </div>
                  <div className="text-lg text-white font-medium">
                    {conflict.sample_artists?.join(' / ') || 'Unknown'} - {conflict.sample_titles?.join(' / ') || 'Unknown'}
                  </div>
                  <div className="text-yellow-400 text-sm mt-1">
                    ⚠️ {conflict.affected_plays} plays affected
                  </div>
                </div>
                
                {/* Manual Override Toggle */}
                <button
                  onClick={() => {
                    if (overrideMode === conflict.normalized_key) {
                      setOverrideMode(null);
                      setCustomArtist('');
                      setCustomTitle('');
                    } else {
                      setOverrideMode(conflict.normalized_key);
                      // PRIORITY: Pre-fill with fingerprint display if available
                      if (conflict.recommended_display) {
                        setCustomArtist(conflict.recommended_display.artist || '');
                        setCustomTitle(conflict.recommended_display.title || '');
                      } else {
                        // Fallback to first option
                        const firstOption = conflict.canonical_options?.[0];
                        if (firstOption) {
                          setCustomArtist(firstOption.display_artist || '');
                          setCustomTitle(firstOption.display_title || '');
                        }
                      }
                    }
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                    overrideMode === conflict.normalized_key
                      ? 'bg-purple-600 text-white'
                      : conflict.recommended_display 
                        ? 'bg-emerald-600 text-white hover:bg-emerald-500 ring-2 ring-emerald-400/50'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <Edit3 className="w-4 h-4" />
                  {conflict.recommended_display ? '✨ Εφαρμογή Fingerprint' : 'Manual Override'}
                </button>
              </div>

              {/* Option 1 & 2: Existing Canonical Options */}
              <div className="mb-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Υπάρχουσες Επιλογές (Canonical Records)</div>
                
                {/* NEW: Show recommended display from fingerprint OR best variation if different from options */}
                {conflict.recommended_display && (
                  <div className={`mb-3 p-3 rounded-lg border-2 ${
                    conflict.recommended_reason?.includes('fingerprint') 
                      ? 'bg-emerald-900/40 border-emerald-400' 
                      : 'bg-cyan-900/40 border-cyan-400'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className={`w-5 h-5 ${
                        conflict.recommended_reason?.includes('fingerprint') ? 'text-emerald-400' : 'text-cyan-400'
                      }`} />
                      <span className={`font-semibold ${
                        conflict.recommended_reason?.includes('fingerprint') ? 'text-emerald-300' : 'text-cyan-300'
                      }`}>
                        {conflict.recommended_reason?.includes('fingerprint') 
                          ? 'Πιστοποιημένη Γραφή (Fingerprint)' 
                          : 'Προτεινόμενη Γραφή (Βάσει Plays & Format)'}
                      </span>
                    </div>
                    <div className="text-white font-medium text-lg">
                      {conflict.recommended_display.artist} - {conflict.recommended_display.title}
                    </div>
                    <div className={`text-xs mt-1 ${
                      conflict.recommended_reason?.includes('fingerprint') ? 'text-emerald-200' : 'text-cyan-200'
                    }`}>
                      {conflict.recommended_reason?.includes('fingerprint') 
                        ? 'Αυτή είναι η σωστή γραφή από audio fingerprinting' 
                        : 'Αυτή η γραφή έχει Title Case format και τα περισσότερα plays'}
                      {' - πατήστε "✨ Εφαρμογή" για να την χρησιμοποιήσετε'}
                    </div>
                  </div>
                )}
                
                <div className="grid gap-2">
                  {conflict.canonical_options?.map((option, idx) => {
                    const isRecommended = conflict.recommended_canonical_id === option.id;
                    return (
                    <div 
                      key={option.id}
                      className={`flex items-center justify-between rounded-lg p-3 border transition-colors ${
                        isRecommended 
                          ? 'bg-green-900/30 border-green-500 ring-2 ring-green-500/50' 
                          : 'bg-gray-700/50 border-gray-600 hover:border-blue-500'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="text-white font-medium flex items-center gap-2">
                          <span className="text-gray-500 text-sm">#{idx + 1}</span>
                          {/* Use recommended_display format when this is the recommended option */}
                          {isRecommended && conflict.recommended_display ? (
                            <span className="text-green-300">
                              {conflict.recommended_display.artist} - {conflict.recommended_display.title}
                            </span>
                          ) : (
                            <span>{option.display_artist} - {option.display_title}</span>
                          )}
                          {isRecommended && (
                            <span className="px-2 py-0.5 bg-green-600 text-green-100 text-xs rounded-full ml-2 flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              Προτείνεται
                              {conflict.recommended_reason === 'fingerprint_exact_match' && ' (Fingerprint)'}
                              {conflict.recommended_reason === 'fingerprint_format_override' && ' (Fingerprint)'}
                              {conflict.recommended_reason === 'best_variation_format' && ' (Best Format)'}
                              {conflict.recommended_reason === 'title_case_format' && ' (Σωστή Γραφή)'}
                              {conflict.recommended_reason === 'user_db_match' && ' (User DB)'}
                            </span>
                          )}
                        </div>
                        {/* Show original format if different from recommended */}
                        {isRecommended && conflict.recommended_display && 
                         (option.display_artist !== conflict.recommended_display.artist || 
                          option.display_title !== conflict.recommended_display.title) && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            Αρχική γραφή: {option.display_artist} - {option.display_title}
                          </div>
                        )}
                        <div className="text-sm text-gray-400 mt-1">
                          <span className="text-blue-400">{option.play_count} plays</span>
                        </div>
                      </div>
                      <button
                        onClick={() => resolveConflict(conflict.normalized_key, option.id, conflict)}
                        disabled={resolving === conflict.normalized_key}
                        className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                          resolving === conflict.normalized_key
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : isRecommended 
                              ? 'bg-green-600 hover:bg-green-500 text-white ring-2 ring-green-400'
                              : 'bg-green-600 hover:bg-green-500 text-white'
                        }`}
                      >
                        {resolving === conflict.normalized_key ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Merge className="w-3 h-3" />
                        )}
                        Επιλογή
                      </button>
                    </div>
                  )})}
                </div>
              </div>

              {/* NEW: All Variations Section - Shows Greek, Greeklish, etc. */}
              {conflict.all_variations?.length > 0 && (
                <div className="mb-3">
                  <button
                    onClick={() => setExpandedVariations(prev => ({
                      ...prev,
                      [conflict.normalized_key]: !prev[conflict.normalized_key]
                    }))}
                    className="text-xs text-cyan-400 uppercase tracking-wide mb-2 flex items-center gap-1 hover:text-cyan-300 cursor-pointer w-full justify-between"
                  >
                    <span className="flex items-center gap-1">
                      <List className="w-3 h-3" />
                      Όλες οι Παραλλαγές ({conflict.all_variations.length} διαφορετικές γραφές)
                    </span>
                    {expandedVariations[conflict.normalized_key] ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {expandedVariations[conflict.normalized_key] && (
                    <div className="grid gap-2 max-h-80 overflow-y-auto">
                      {conflict.all_variations.map((variation, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between bg-cyan-900/20 rounded-lg p-3 border border-cyan-700/40 hover:border-cyan-500 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="text-cyan-200 font-medium flex items-center gap-2">
                              <span className="text-cyan-500 text-xs">#{idx + 1}</span>
                              {variation.display}
                            </div>
                            <div className="text-sm text-cyan-300/60 mt-1">
                              {variation.play_count} plays
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setOverrideMode(conflict.normalized_key);
                              setCustomArtist(variation.artist);
                              setCustomTitle(variation.title);
                            }}
                            className="px-3 py-1.5 rounded-lg text-sm bg-cyan-700 hover:bg-cyan-600 text-white flex items-center gap-1"
                            title="Χρήση αυτής της γραφής"
                          >
                            <Edit3 className="w-3 h-3" />
                            Χρήση
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Option 3: User DB Suggestions */}
              <div className="mb-3">
                <div className="text-xs text-purple-400 uppercase tracking-wide mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <Database className="w-3 h-3" />
                    Από User Song DB (Playlist Analyzer)
                  </span>
                  <button
                    onClick={() => {
                      // Clear cache and refetch
                      setUserDbSuggestions(prev => {
                        const newState = {...prev};
                        delete newState[conflict.normalized_key];
                        return newState;
                      });
                      fetchUserDbSuggestion(conflict);
                    }}
                    className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Ανανέωση
                  </button>
                </div>
                
                {loadingSuggestions[conflict.normalized_key] ? (
                  <div className="text-purple-400 text-sm flex items-center gap-2 p-3 bg-purple-900/20 rounded-lg">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    {t('searchingUserDb')}
                  </div>
                ) : userDbSuggestions[conflict.normalized_key]?.length > 0 ? (
                  <div className="grid gap-2">
                    {userDbSuggestions[conflict.normalized_key].map((suggestion, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between bg-purple-900/30 rounded-lg p-3 border border-purple-600/50 hover:border-purple-400 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="text-purple-200 font-medium flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-purple-400" />
                            {suggestion.display}
                          </div>
                          {suggestion.year && (
                            <div className="text-sm text-purple-300/60 mt-1">
                              Year: {suggestion.year}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Use for Manual Override */}
                          <button
                            onClick={() => {
                              setOverrideMode(conflict.normalized_key);
                              applySuggestion(suggestion);
                            }}
                            className="px-3 py-1.5 rounded-lg text-sm bg-gray-600 hover:bg-gray-500 text-white flex items-center gap-1"
                            title="Χρήση στο Manual Override"
                          >
                            <Edit3 className="w-3 h-3" />
                            Edit
                          </button>
                          {/* Direct Selection - Apply immediately */}
                          <button
                            onClick={() => resolveWithUserDbSuggestion(conflict.normalized_key, suggestion)}
                            disabled={resolving === conflict.normalized_key}
                            className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${
                              resolving === conflict.normalized_key
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-purple-600 hover:bg-purple-500 text-white'
                            }`}
                            title="Επιλογή αυτής της γραφής"
                          >
                            {resolving === conflict.normalized_key ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Merge className="w-3 h-3" />
                            )}
                            Επιλογή
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm p-3 bg-gray-700/30 rounded-lg italic">
                    Δεν βρέθηκαν προτάσεις στο User Song DB
                  </div>
                )}
              </div>

              {/* Option 4: Manual Override Panel */}
              {overrideMode === conflict.normalized_key && (
                <div className="mt-4 p-4 bg-gradient-to-r from-purple-900/40 to-blue-900/40 rounded-lg border border-purple-500/50">
                  <div className="text-sm text-purple-300 mb-3 flex items-center gap-2">
                    <Edit3 className="w-4 h-4" />
                    <strong>Manual Override</strong> - Γράψτε τη σωστή γραφή
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Artist</label>
                      <input
                        type="text"
                        value={customArtist}
                        onChange={(e) => setCustomArtist(e.target.value)}
                        className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white focus:border-purple-500 focus:outline-none"
                        placeholder="π.χ. ANNA VISSI"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Title</label>
                      <input
                        type="text"
                        value={customTitle}
                        onChange={(e) => setCustomTitle(e.target.value)}
                        className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white focus:border-purple-500 focus:outline-none"
                        placeholder="π.χ. ΛΕΚΕΣ"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => resolveWithOverride(conflict.normalized_key)}
                      disabled={resolving === conflict.normalized_key || !customArtist.trim() || !customTitle.trim()}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        resolving === conflict.normalized_key || !customArtist.trim() || !customTitle.trim()
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-600 hover:bg-purple-500 text-white'
                      }`}
                    >
                      {resolving === conflict.normalized_key ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Δημιουργία & Επίλυση
                    </button>
                    
                    <button
                      onClick={() => {
                        setOverrideMode(null);
                        setCustomArtist('');
                        setCustomTitle('');
                      }}
                      className="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600"
                    >
                      Ακύρωση
                    </button>
                  </div>
                  
                  <div className="text-xs text-gray-500 mt-2">
                    Θα δημιουργηθεί νέο canonical με τη γραφή που δώσατε και θα συγχωνευτούν όλα τα plays.
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CanonicalConflictResolver;
